/**
 * 基础组件库语言桥接统一入口
 *
 * 放置位置：业务应用启动入口（src/main.js / src/entry/<xxx>-main.js）
 * 调用时机：
 *   - applyLibLocale()：必须在 new Vue(...) 之前调用一次
 *   - onLibLocaleChange()：注册一次，订阅 qidianLocale 运行时变化
 *
 * 设计约束（不要违反）：
 *   - 不在业务组件 / 中间层业务组件库（vcs / wcs / cusbase 等）里调用
 *   - 不要在运行时反复调用 applyLibLocale（语言是全局单例）
 *   - 运行时切语言默认走整页刷新策略；如需无刷新切换，请在 onLibLocaleChange 中
 *     自行追加 root 组件 key 重建逻辑
 */

import { getQidianLocale, onLocaleChange } from '../locale-reader';
import { applyVhtmlLocale } from './vhtml-bridge';
import { applyVhtmlMarketLocale } from './vhtml-market-bridge';
// 如后续新增其他需要桥接的基础库（例如 cusbase 自治 i18n），在这里追加：
// import { applyCusbaseLocale } from './cusbase-bridge';

/**
 * 按当前 qidianLocale 一次性应用到所有基础组件库
 * 强烈建议在 `new Vue({...})` 之前调用，并等待返回的 Promise resolve 后再渲染，
 * 以确保英文环境首帧 vhtml 组件文案就绪。
 * 如入口无法改为异步，可在 HTML 模板里手动预加载 locale/en.js（bridge 检测到
 * window.vhtml.lang.en 已存在后走同步路径，Promise 立即 resolve）。
 *
 * @param {string} [locale]
 * @returns {Promise<void>} resolve 后保证 vhtml 语言包已就绪
 */
export function applyLibLocale(locale) {
    const current = locale || getQidianLocale();
    const p = applyVhtmlLocale(current);
    applyVhtmlMarketLocale(current);
    // applyCusbaseLocale(current);
    return p;
}

/**
 * 订阅 qidianLocale 变化，自动同步所有基础组件库
 * 建议整个应用只注册一次
 *
 * @param {(locale: string) => void} [onAfter] 可选：语言同步完成后触发的回调
 *                典型用法：location.reload() 或 root 组件 key++ 重建
 * @returns {() => void} 取消订阅函数
 */
export function onLibLocaleChange(onAfter) {
    return onLocaleChange((locale) => {
        applyLibLocale(locale);
        if (onAfter) onAfter(locale);
    });
}
